import { h } from "../lib/dom";
import { formatDate, formatDateTime } from "../lib/format";
import { supabase, type ReviewRow } from "../lib/supabase";

export interface DashboardOptions {
  onOpen: (reviewId: string) => void;
  onCreate: () => void;
}

export function renderDashboard(opts: DashboardOptions): HTMLElement {
  const listEl = h("div", { class: "rows" }, h("p", { class: "rows-empty" }, "Loading drafts…"));

  void loadReviews(listEl, opts);

  return h(
    "main",
    { class: "page" },
    h(
      "header",
      { class: "page-head" },
      h(
        "div",
        {},
        h("p", { class: "eyebrow" }, "Drafts &amp; published" as unknown as string),
        h("h1", { class: "page-title" }, "All reviews"),
      ),
      h(
        "button",
        {
          class: "btn btn-primary",
          type: "button",
          onclick: () => opts.onCreate(),
        },
        "New review",
      ),
    ),
    listEl,
  );
}

async function loadReviews(listEl: HTMLElement, opts: DashboardOptions): Promise<void> {
  const { data, error } = await supabase
    .from("reviews")
    .select(
      "id,restaurant_name,city,neighborhood,published,final_score,date_visited,updated_at",
    )
    .order("updated_at", { ascending: false })
    .limit(100);

  if (error) {
    listEl.replaceChildren(
      h("p", { class: "rows-error" }, `Could not load reviews: ${error.message}`),
    );
    return;
  }

  const rows = (data ?? []) as Pick<
    ReviewRow,
    "id" | "restaurant_name" | "city" | "neighborhood" | "published" | "final_score" | "date_visited" | "updated_at"
  >[];

  if (rows.length === 0) {
    listEl.replaceChildren(
      h(
        "p",
        { class: "rows-empty" },
        "No reviews yet. Start one to capture the first restaurant.",
      ),
    );
    return;
  }

  listEl.replaceChildren(
    ...rows.map((row) =>
      h(
        "button",
        {
          class: "row",
          type: "button",
          onclick: () => opts.onOpen(row.id),
        },
        h(
          "div",
          { class: "row-main" },
          h("p", { class: "row-title" }, row.restaurant_name || "Untitled review"),
          h(
            "p",
            { class: "row-sub" },
            [row.neighborhood, row.city].filter(Boolean).join(" · ") || "No location yet",
          ),
        ),
        h(
          "div",
          { class: "row-meta" },
          h(
            "span",
            { class: `row-status${row.published ? " is-published" : ""}` },
            row.published ? "Published" : "Draft",
          ),
          h(
            "span",
            { class: "row-score" },
            row.final_score !== null ? `${row.final_score.toFixed(1)} / 10` : "— / 10",
          ),
          h(
            "span",
            { class: "row-date" },
            row.date_visited ? `Visited ${formatDate(row.date_visited)}` : `Updated ${formatDateTime(row.updated_at)}`,
          ),
        ),
      ),
    ),
  );
}
